import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { API, EASE } from "@/lib/site";

function RsvpModal({ event, onClose }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", guests: 1, member: "", attendees: "", address: "", city: "", zip: "", dietary: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const { first_name, last_name, member, ...rest } = form;
      await axios.post(`${API}/rsvp`, {
        ...rest,
        name: `${first_name} ${last_name}`.trim(),
        member: member === "" ? null : member === "yes",
        guests: Number(form.guests) || 1,
        event_id: event.id,
      });
      setState("sent");
      toast.success("See you there!", { description: `Your RSVP for ${event.title} is in.` });
      setTimeout(onClose, 1600);
    } catch (err) {
      setState("idle");
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong", {
        description: typeof detail === "string" ? "Contact the temple office to join a waitlist." : "Please try again or call the temple office.",
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center px-6 bg-ink/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="rsvp-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="w-full max-w-md max-h-[85vh] overflow-y-auto border border-gold/40 bg-surface p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-2">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gold">RSVP</p>
          <button onClick={onClose} data-testid="rsvp-close-button" aria-label="Close" className="text-foreground/50 hover:text-gold transition-colors">
            <X size={18} />
          </button>
        </div>
        <h3 className="font-syne font-bold uppercase tracking-tight text-xl mb-2">{event.title}</h3>
        <p className="text-xs text-foreground/50 mb-8">{event.weekday} · {event.month} {event.day} · {event.time}</p>
        <form onSubmit={submit} className="space-y-6" data-testid="rsvp-form">
          <div className="grid grid-cols-2 gap-6">
            <input data-testid="rsvp-name-input" required value={form.first_name} onChange={set("first_name")} placeholder="First name" className="input-brutal !py-3 text-sm" />
            <input data-testid="rsvp-last-name-input" required value={form.last_name} onChange={set("last_name")} placeholder="Last name" className="input-brutal !py-3 text-sm" />
          </div>
          <input data-testid="rsvp-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className="input-brutal !py-3 text-sm" />
          <div className="grid grid-cols-2 gap-6">
            <input data-testid="rsvp-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Phone *" className="input-brutal !py-3 text-sm" />
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-foreground/40">Guests (incl. you)</span>
              <input data-testid="rsvp-guests-input" type="number" min={1} max={20} value={form.guests} onChange={set("guests")} className="input-brutal !py-3 text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-foreground/40 mb-1">Are you a Beth El member? *</span>
            <select data-testid="rsvp-member-select" required value={form.member} onChange={set("member")} className="input-brutal !py-3 text-sm w-full bg-surface">
              <option value="">Select one…</option>
              <option value="yes">Yes, I'm a member</option>
              <option value="no">Not yet / guest</option>
            </select>
          </label>
          <textarea data-testid="rsvp-attendees-input" rows={2} value={form.attendees} onChange={set("attendees")} placeholder="Names of everyone attending (optional)" className="input-brutal !py-3 text-sm w-full resize-none" />
          <input data-testid="rsvp-dietary-input" value={form.dietary} onChange={set("dietary")} placeholder="Dietary needs (optional)" className="input-brutal !py-3 text-sm" />
          <input data-testid="rsvp-address-input" value={form.address} onChange={set("address")} placeholder="Street address (optional)" className="input-brutal !py-3 text-sm" />
          <div className="grid grid-cols-2 gap-6">
            <input data-testid="rsvp-city-input" value={form.city} onChange={set("city")} placeholder="City" className="input-brutal !py-3 text-sm" />
            <input data-testid="rsvp-zip-input" value={form.zip} onChange={set("zip")} placeholder="ZIP" className="input-brutal !py-3 text-sm" />
          </div>
          <button
            data-testid="rsvp-submit-button"
            type="submit"
            disabled={state === "sending"}
            className={`w-full px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium transition-colors duration-400 ${
              state === "sent" ? "border border-gold text-gold" : "bg-gold text-inkdeep hover:bg-gold-light disabled:opacity-60"
            }`}
          >
            {state === "sending" ? "Sending…" : state === "sent" ? "See you there" : "Reserve My Spot"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function Events() {
  const [events, setEvents] = useState(null);
  const [rsvpEvent, setRsvpEvent] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/events`)
      .then((r) => {
        // hide events automatically once their date has passed (Eastern time)
        const today = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
        setEvents(
          r.data
            .filter((e) => (e.sort || "") >= today && !e.calendar_only)
            .sort((a, b) => (a.sort || "").localeCompare(b.sort || ""))
        );
      })
      .catch(() => setEvents([]));
  }, []);

  return (
    <section id="events" data-testid="events-section" className="py-32 md:py-48 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-20">
          <div>
            <p className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-gold mb-4">Calendar</p>
            <h2 className="font-syne font-bold uppercase tracking-tighter leading-[0.95] text-4xl md:text-6xl">
              Upcoming <span className="text-gold">events</span>
            </h2>
          </div>
          <a
            data-testid="events-calendar-link"
            href="https://www.bethelwpb.com/cal.php"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs tracking-[0.25em] uppercase text-foreground/60 hover:text-gold transition-colors duration-300"
          >
            Full Calendar →
          </a>
        </Reveal>

        {events === null ? (
          <div className="space-y-4" data-testid="events-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-surface animate-pulse border border-white/5" />
            ))}
          </div>
        ) : (
          <div>
            {events.map((e, i) => (
              <Reveal key={e.id} delay={0.04 * i} y={24}>
                <div
                  data-testid={`event-row-${e.id}`}
                  className="group grid grid-cols-[auto_1fr] md:grid-cols-[120px_1fr_auto] items-center gap-5 md:gap-10 border-b border-white/10 py-7 md:py-9 hover:border-gold/50 transition-colors duration-500"
                >
                  <div className="text-center md:text-left">
                    <span className="block font-syne font-extrabold text-3xl md:text-5xl leading-none text-gold">{e.day}</span>
                    <span className="block text-[10px] tracking-[0.3em] uppercase text-foreground/50 mt-2">
                      {e.weekday} · {e.month}
                    </span>
                  </div>
                  <a href={e.link} target={e.link.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="min-w-0 block">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">{e.category}</span>
                    <h3 className="font-syne font-bold uppercase tracking-tight text-lg md:text-2xl mt-1 group-hover:text-gold transition-colors duration-500">
                      {e.title}
                    </h3>
                    <p className="text-sm text-foreground/50 mt-2 max-w-2xl hidden md:block">{e.description}</p>
                    <p className="text-xs text-foreground/40 mt-1 md:mt-2">{e.time}</p>
                  </a>
                  <div className="col-span-2 md:col-span-1 flex md:flex-col items-center md:items-end gap-4">
                    {e.capacity && e.seats_left === 0 ? (
                      <span
                        data-testid={`soldout-${e.id}`}
                        className="px-6 py-2.5 border border-white/15 text-foreground/40 text-[10px] tracking-[0.25em] uppercase"
                      >
                        Sold Out
                      </span>
                    ) : e.rsvp_url ? (
                      <a
                        href={e.rsvp_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`tickets-link-${e.id}`}
                        className="px-6 py-2.5 border border-gold/50 text-gold text-[10px] tracking-[0.25em] uppercase hover:bg-gold hover:text-inkdeep transition-colors duration-400"
                      >
                        Tickets
                      </a>
                    ) : (
                      <button
                        data-testid={`rsvp-button-${e.id}`}
                        onClick={() => setRsvpEvent(e)}
                        className="px-6 py-2.5 border border-gold/50 text-gold text-[10px] tracking-[0.25em] uppercase hover:bg-gold hover:text-inkdeep transition-colors duration-400"
                      >
                        RSVP
                      </button>
                    )}
                    {e.capacity != null && e.seats_left > 0 && (
                      <span data-testid={`seats-left-${e.id}`} className="text-[10px] tracking-[0.2em] uppercase text-foreground/40">
                        {e.seats_left} of {e.capacity} spots left
                      </span>
                    )}
                    <a href={e.link} target={e.link.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" aria-label={`More about ${e.title}`}>
                      <ArrowUpRight
                        size={26}
                        className="text-foreground/30 group-hover:text-gold transition-[color,transform] duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {rsvpEvent && <RsvpModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} />}
      </AnimatePresence>
    </section>
  );
}
